import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pen } from 'lucide-react';
import Navigation from '../components/Navigation';
import ExhibitModal from '../components/ExhibitModal';
import PageHeader from '../components/PageHeader';
import { useExhibit } from '../context/ExhibitContext';
import { getImageUrl } from '../scripts/utils';
import './MyExhibits.css';

function MyExhibits({ textSize, onTextSizeChange }) {
  const { exhibits, createExhibit, updateExhibit } = useExhibit();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingExhibit, setEditingExhibit] = useState(null);

  const handleCreateExhibit = (exhibitData) => {
    createExhibit(exhibitData.name, exhibitData.description);
    setIsModalOpen(false);
  };

  const handleEditExhibit = (exhibitData) => {
    updateExhibit(editingExhibit.id, exhibitData);
    setEditingExhibit(null);
    setIsModalOpen(false);
  };

  const openEditModal = (exhibit) => {
    setEditingExhibit(exhibit);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingExhibit(null);
  };

  return (
    <div className="my-exhibits">
      <a href="#main-content" className="skip-nav">Skip to main content</a>
  <Navigation 
    currentPage="my-exhibits" 
    textSize={textSize} 
    onTextSizeChange={onTextSizeChange}
  />

      <PageHeader 
        title="My Exhibits"
        description="Welcome to your personal exhibition space! Here you can create and manage your own curated art collections. Click the arrow next to any exhibit to expand and get a closer look at your curated masterpieces! You can also edit your exhibit by clicking the ✏️ icon. Go wild and make your own mini-museum!"
        showButton={true}
        buttonText="Create New Exhibit"
        buttonIcon={<Plus size={20} />}
        onButtonClick={() => setIsModalOpen(true)}
        buttonClassName="load-more-btn header-btn"
        textSize={textSize}
      />

      <main className="exhibits-container" role="main" id="main-content">
        <section className="exhibits-content" aria-label="Exhibits content">
          {exhibits.length > 0 ? (
            <div className="exhibits-grid">
              {exhibits.map((exhibit) => (
                <article key={exhibit.id} className="exhibit-card expanded">
                  <div className="exhibit-card-header">
                    <h2 className="exhibit-title">{exhibit.name}</h2>
                    <button
                      className="edit-icon-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditModal(exhibit);
                      }}
                      aria-label={`Edit ${exhibit.name}`}
                      title="Edit exhibit"
                    >
                      <Pen size={16} />
                    </button>
                  </div>

                  <div className="artwork-preview-area">
                    {exhibit.artworks && exhibit.artworks.length > 0 ? (
                      <div className="expanded-artworks">
                        <div className="exhibit-description">
                          {exhibit.description && (
                            <p className="description-text">{exhibit.description}</p>
                          )}
                          <p className="artwork-count">{exhibit.artworks.length} artwork{exhibit.artworks.length !== 1 ? 's' : ''}</p>
                        </div>
                        <div className="expanded-artworks-grid">
                          {exhibit.artworks.map((artwork) => (
                            <div key={`${exhibit.id}-${artwork.id}-expanded`} className="expanded-artwork-card">
                              <div className="expanded-artwork-image">
                                <img
                                  src={getImageUrl(artwork.image_id)}
                                  alt={`${artwork.title} by ${artwork.artist}, ${artwork.year}`}
                                  className="artwork-image"
                                  loading="lazy"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              </div>
                              <div className="expanded-artwork-info">
                                <h4 className="expanded-artwork-title">{artwork.title}</h4>
                                <p className="expanded-artwork-meta">{artwork.artist} • {artwork.year}</p>
                                {artwork.tags && artwork.tags.length > 0 && (
                                  <div className="expanded-artwork-tags">
                                    {artwork.tags.map((tag, index) => (
                                      <span key={index} className={`tag ${tag.toLowerCase().replace(/\s+/g, '-')}`}>
                                        {tag.toUpperCase()}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                          <Link
                            to="/"
                            className="add-artwork-btn expanded"
                            aria-label={`Add artwork to ${exhibit.name}`}
                            title="Add artwork to this exhibit"
                          >
                            <div className="add-artwork-icon">+</div>
                            <span className="add-artwork-text">Add More Artwork</span>
                          </Link>
                        </div>
                      </div>
                    ) : (
                      <Link
                        to="/"
                        className="add-artwork-btn empty"
                        aria-label={`Add artwork to ${exhibit.name}`}
                        title="Add artwork to this exhibit"
                      >
                        <div className="add-artwork-icon">+</div>
                        <span className="add-artwork-text">Add Artwork</span>
                      </Link>
                    )}
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="empty-state" aria-live="polite">
              <div className="empty-icon">🎨</div>
              <h2>No exhibits yet</h2>
              <p>Start curating your first exhibit by creating a collection and adding artworks.</p>
            </div>
          )}
        </section>
      </main>

      <ExhibitModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSave={editingExhibit ? handleEditExhibit : handleCreateExhibit}
        exhibit={editingExhibit}
        mode={editingExhibit ? 'edit' : 'create'}
      />
    </div>
  );
}

export default MyExhibits;
